import { Config, ProjectPayload } from "atoms/Ltool/newConfigAtoms";
import type { Powertrain } from "atoms/powertrainAtom";
import { throwIfNotOk } from "./apiError";

// powertrain セグメント（/assess/<powertrain>/...）は引数で切替（既定 "bev"）。
// gear を含むエンドポイント（dimensions/achievement/copy）は gear 対応版を別途用意し、
// ここでは gear 不要の計算セット CRUD のみ prefix を引数化する。

// レスポンス契約（issue #34）。L は全登録経路で単一型（LRegistrationBatchResponse）。
export type FailedCopyItem = {
    unit_id?: string | null;
    gear_id?: string | null;
    condition_id?: string | null;
    reason?: string | null;
    detail?: string | null;
};

export type LRegistrationBatchResponse = {
    status: number;
    message?: string;
    summary?: { succeeded: number; failed: number; skipped: number };
    failed_items?: FailedCopyItem[];
    warnings?: unknown[];
};

// 計算セット新規作成
export const submitProject = async (
    authToken: string,
    config: Config,
    payload: ProjectPayload,
    powertrain: Powertrain = "bev",
) => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/assess/${powertrain}/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.calculation_id)}`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        },
        body: JSON.stringify(payload),
    });

    await throwIfNotOk(response, "createCalcSet");
    const body = await response.json();
    return body.message;
};

// 作成済み計算セットのdescription編集
export const patchCalculationSetDescription = async (
    authToken: string,
    config: Config,
    description: string,
    powertrain: Powertrain = "bev",
) => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/assess/${powertrain}/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.calculation_id)}`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        },
        body: JSON.stringify({ description }),
    });

    await throwIfNotOk(response, "patchCalcSetDesc");
    const body = await response.json();
    return body.message;
};

// 計算セット削除
export const deleteCalculationSet = async (
    authToken: string,
    config: Config,
    powertrain: Powertrain = "bev",
) => {
    const baseURL = process.env.REACT_APP_URL;
    const url = `${baseURL}/assess/${powertrain}/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.calculation_id)}`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        },
    });

    await throwIfNotOk(response, "deleteCalcSet");
    const body = await response.json();
    return body.message;
};

// 他計算セットから全データコピー登録（BEV）。
// HEV は gear 付き URL が正であり、submitAllCopyProjectHev を gear ごとに呼ぶ。
export const submitAllCopyProject = async (
    authToken: string,
    config: Config,
    fromConfig: Config,
    type: string = "target",
    powertrain: Powertrain = "bev",
): Promise<LRegistrationBatchResponse> => {
    if (powertrain !== "bev") {
        throw new Error("submitAllCopyProject is BEV-only. Use submitAllCopyProjectHev for HEV all-copy.");
    }

    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/assess/bev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.calculation_id)}
    /datasets/all?type=${encodeURIComponent(type)}`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        },
        body: JSON.stringify(
            {
                "registration_method": "copy",
                "config": {
                    "project_id": fromConfig.project_id,
                    "destination_id": fromConfig.destination_id,
                    "lot_id": fromConfig.lot_id,
                    "phase_id": fromConfig.phase_id,
                    "calculation_id": fromConfig.calculation_id,
                    // #22: BEV/HEV 共存時の曖昧性を解消するため powertrain を明示送信する
                    "powertrain": "bev",
                }
            }
        ),
    });

    const body = await response.json().catch(() => ({}));
    const payload = typeof body === "object" && body !== null ? body : {};
    return { status: response.status, ...payload } as LRegistrationBatchResponse;
};

// 他計算セットからユニット別データコピー登録
export const submitUnitCopyProject = async (
    authToken: string,
    config: Config,
    fromConfig: Config,
    unit_id: string,
    type: string = "target",
): Promise<LRegistrationBatchResponse> => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/assess/bev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.calculation_id)}
    /datasets/units
    /${encodeURIComponent(unit_id)}
    ?type=${encodeURIComponent(type)}`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        },
        body: JSON.stringify(
            {
                "registration_method": "copy",
                "config": {
                    "project_id": fromConfig.project_id,
                    "destination_id": fromConfig.destination_id,
                    "lot_id": fromConfig.lot_id,
                    "phase_id": fromConfig.phase_id,
                    "calculation_id": fromConfig.calculation_id,
                    // #22: BEV 専用エンドポイント。powertrain を明示して共存時の曖昧性を解消
                    "powertrain": "bev",
                }
            }
        ),
    });

    const body = await response.json().catch(() => ({}));
    const payload = typeof body === "object" && body !== null ? body : {};
    return { status: response.status, ...payload } as LRegistrationBatchResponse;
};

// 他計算セットから個別データコピー登録
export const submitSubCopyProject = async (
    authToken: string,
    config: Config,
    fromConfig: Config,
    unit_id: string,
    condition_group_id: string,
    type: string = "target",
): Promise<LRegistrationBatchResponse> => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/assess/bev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.calculation_id)}
    /datasets/units
    /${encodeURIComponent(unit_id)}
    /${encodeURIComponent(condition_group_id)}
    ?type=${encodeURIComponent(type)}`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        },
        body: JSON.stringify(
            {
                "registration_method": "copy",
                "config": {
                    "project_id": fromConfig.project_id,
                    "destination_id": fromConfig.destination_id,
                    "lot_id": fromConfig.lot_id,
                    "phase_id": fromConfig.phase_id,
                    "calculation_id": fromConfig.calculation_id,
                    // #22: BEV 専用エンドポイント。powertrain を明示して共存時の曖昧性を解消
                    "powertrain": "bev",
                }
            }
        ),
    });

    const body = await response.json().catch(() => ({}));
    const payload = typeof body === "object" && body !== null ? body : {};
    return { status: response.status, ...payload } as LRegistrationBatchResponse;
};

// Excelから全データコピー登録
export const submitAllCopyExcel = async (
    authToken: string,
    config: Config,
    type: string,
): Promise<LRegistrationBatchResponse> => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/assess/bev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.calculation_id)}
    /datasets/all?type=${encodeURIComponent(type)}`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        },
        body: JSON.stringify(
            {
                "registration_method": "excel",
            }
        ),
    });

    const body = await response.json().catch(() => ({}));
    const payload = typeof body === "object" && body !== null ? body : {};
    return { status: response.status, ...payload } as LRegistrationBatchResponse;
};

// Excelからユニット別データコピー登録
export const submitUnitCopyExcel = async (
    authToken: string,
    config: Config,
    unit_id: string,
    type: string,
): Promise<LRegistrationBatchResponse> => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/assess/bev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.calculation_id)}
    /datasets/units
    /${encodeURIComponent(unit_id)}
    ?type=${encodeURIComponent(type)}`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        },
        body: JSON.stringify(
            {
                "registration_method": "excel",
            }
        ),
    });

    const body = await response.json().catch(() => ({}));
    const payload = typeof body === "object" && body !== null ? body : {};
    return { status: response.status, ...payload } as LRegistrationBatchResponse;
};

// Excelから個別データコピー登録
export const submitSubCopyExcel = async (
    authToken: string,
    config: Config,
    unit_id: string,
    condition_group_id: string,
    type: string,
): Promise<LRegistrationBatchResponse> => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/assess/bev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.calculation_id)}
    /datasets/units
    /${encodeURIComponent(unit_id)}
    /${encodeURIComponent(condition_group_id)}
    ?type=${encodeURIComponent(type)}`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        },
        body: JSON.stringify(
            {
                "registration_method": "excel",
            }
        ),
    });

    const body = await response.json().catch(() => ({}));
    const payload = typeof body === "object" && body !== null ? body : {};
    return { status: response.status, ...payload } as LRegistrationBatchResponse;
};

// 諸元: gear_ratio / motor_order とも {phenomenon_id: float} のフラット形式（BEV/HEV共通）。
// gear は HEV のみ URL から確定し、いずれも drive_mode には依存しない
// （諸元データ仕様変更 #26。BEV も HevDimensions.from_partial_dict と同じ Dict[phenomenon_id] 形式に統一）。
export type DimensionParams = {
    tire_radius: number;
    gear_ratio: Record<string, number>;
    motor_order: Record<string, number>;
};

export type HevDimensionParams = DimensionParams;

// 諸元データ登録(UI入力)
export const submitDimensions = async (
    authToken: string,
    config: Config,
    unit_id: string,
    params: DimensionParams,
): Promise<LRegistrationBatchResponse> => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/assess/bev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.calculation_id)}
    /datasets/units
    /${encodeURIComponent(unit_id)}
    /dimensions/all`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        },
        body: JSON.stringify(
            {
                registration_method: "ui",
                dimensions: {
                    tire_radius: params.tire_radius,
                    gear_ratio: params.gear_ratio,
                    motor_order: params.motor_order,
                },
            }
        ),
    });

    const body = await response.json().catch(() => ({}));
    const payload = typeof body === "object" && body !== null ? body : {};
    return { status: response.status, ...payload } as LRegistrationBatchResponse;
};

// L 計算 API（達成度判断・周波数変換）の応答: unit × condition 単位で一括計算した
// 結果の 4 分類サマリ（諸元データ仕様変更 #32 / BE #90）。R 側 CalculationSummary に
// 対し、L は複数 unit を扱うため各要素に unit_id が付く（gear は HEV では URL 固定のため
// エントリには含まれない）。FE は unit×condition の列挙のみ表示し、reason /
// missing_dataset_ids は画面に出さない（開発者向けログのみ）。
export type LCalculationSummary = {
    succeeded: { unit_id: string; condition_id: string }[];
    failed: { unit_id: string; condition_id: string; reason: string }[];
    missing_data: { unit_id: string; condition_id: string; missing_dataset_ids: string[] }[];
    skipped_no_data: { unit_id: string; condition_id: string }[];
};

// 達成度計算 POST の body。BE は three_sigma / risk_margin とも powertrain の現象グループ
// すべてのキーを必須とし、欠落・余分なキー・非数値を拒否する（risk_margin は 0 以上）。
// 入力途中を許す null はここに持ち込まない（utils/achievementConfigForm で検証して変換する）。
export type AchievementLevelRequestBody = {
    three_sigma: Record<string, number>;
    risk_margin: Record<string, number>;
    freq: Record<string, { min: number; max: number }>;
};

// 達成度の計算（unit×condition 一括。原則 200 + 4 分類サマリ。4xx/5xx は前提エラーのみ）
export const submitAchievementLevelCalculation = async (
    authToken: string,
    config: Config,
    payload: AchievementLevelRequestBody,
): Promise<LCalculationSummary> => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/assess/bev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.calculation_id)}
    /achievement_level`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        },
        body: JSON.stringify(payload),
    });

    await throwIfNotOk(response, "calcAchievementLevel");

    return await response.json();
};

// 周波数変換（unit×condition 一括。原則 200 + 4 分類サマリ）
export const submitFrequencyConversionCalculation = async (
    authToken: string,
    config: Config,
): Promise<LCalculationSummary> => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/assess/bev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.calculation_id)}
    /frequency-conversion`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        },
    });

    await throwIfNotOk(response, "calcFrequencyConversion");

    return await response.json();
};

// ============================================================
// HEV 専用（L 階層は calc→unit→gear→condition。openapi assess.yaml の HEV path に対応）
// 諸元は .../units/<unit>/<gear>/dimensions/all、達成度計算/周波数変換は gear 単位 URL。
// 達成度計算の freq は BEV と同形のフラット（Cycle2仕様変更_part1）。諸元の
// gear_ratio / motor_order は BEV/HEV 共通の {phenomenon_id: float} フラット形式（諸元データ仕様変更 #26）。
// ============================================================

// HEV: 諸元データ登録(UI入力) — unit 配下に gear。
// body の gear_ratio / motor_order は {phenomenon_id: float} フラット形式（HevDimensionParams）。
export const submitDimensionsHev = async (
    authToken: string,
    config: Config,
    unit_id: string,
    gear_id: string,
    params: HevDimensionParams,
): Promise<LRegistrationBatchResponse> => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/assess/hev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.calculation_id)}
    /datasets/units
    /${encodeURIComponent(unit_id)}
    /${encodeURIComponent(gear_id)}
    /dimensions/all`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        },
        body: JSON.stringify(
            {
                registration_method: "ui",
                dimensions: {
                    tire_radius: params.tire_radius,
                    gear_ratio: params.gear_ratio,
                    motor_order: params.motor_order,
                },
            }
        ),
    });

    const body = await response.json().catch(() => ({}));
    const payload = typeof body === "object" && body !== null ? body : {};
    return { status: response.status, ...payload } as LRegistrationBatchResponse;
};

// HEV: 達成度の計算 — gear 単位 URL（指定 gear のみ保存・計算。body は BEV と同じ形で、
// グループは vib / gn の 2 つ。gear は URL に含め body には含めない）
export const submitAchievementLevelCalculationHev = async (
    authToken: string,
    config: Config,
    gear_id: string,
    payload: AchievementLevelRequestBody,
): Promise<LCalculationSummary> => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/assess/hev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.calculation_id)}
    /${encodeURIComponent(gear_id)}
    /achievement_level`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        },
        body: JSON.stringify(payload),
    });

    await throwIfNotOk(response, "calcAchievementLevel");

    return await response.json();
};

// HEV: 周波数変換 — gear 単位 URL（指定 gear の全 unit × 全 condition を最後まで処理し
// 4 分類サマリを 200 で返す。BE #90 で fail-fast の途中停止は廃止された）
export const submitFrequencyConversionCalculationHev = async (
    authToken: string,
    config: Config,
    gear_id: string,
): Promise<LCalculationSummary> => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/assess/hev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.calculation_id)}
    /${encodeURIComponent(gear_id)}
    /frequency-conversion`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        },
    });

    await throwIfNotOk(response, "calcFrequencyConversion");

    return await response.json();
};

// HEV: Excel から全データコピー登録 — .../datasets/<gear>/all（gn/vib 一括）
export const submitAllCopyExcelHev = async (
    authToken: string,
    config: Config,
    gear_id: string,
    type: string,
): Promise<LRegistrationBatchResponse> => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/assess/hev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.calculation_id)}
    /datasets/${encodeURIComponent(gear_id)}/all?type=${encodeURIComponent(type)}`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        },
        body: JSON.stringify({ registration_method: "excel" }),
    });

    const body = await response.json().catch(() => ({}));
    const payload = typeof body === "object" && body !== null ? body : {};
    return { status: response.status, ...payload } as LRegistrationBatchResponse;
};

// HEV: Excel からユニット別データコピー登録 — .../datasets/units/<unit>/<gear>（gn/vib 一括）
// BEV と比べ <unit_id> 直後に <gear_id> が挿入される（openapi assess.yaml HEV path）。
export const submitUnitCopyExcelHev = async (
    authToken: string,
    config: Config,
    gear_id: string,
    unit_id: string,
    type: string,
): Promise<LRegistrationBatchResponse> => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/assess/hev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.calculation_id)}
    /datasets/units
    /${encodeURIComponent(unit_id)}
    /${encodeURIComponent(gear_id)}
    ?type=${encodeURIComponent(type)}`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        },
        body: JSON.stringify({ "registration_method": "excel" }),
    });

    const body = await response.json().catch(() => ({}));
    const payload = typeof body === "object" && body !== null ? body : {};
    return { status: response.status, ...payload } as LRegistrationBatchResponse;
};

// HEV: Excel から個別データコピー登録 — .../datasets/units/<unit>/<gear>/<condition_group_id>
export const submitSubCopyExcelHev = async (
    authToken: string,
    config: Config,
    gear_id: string,
    unit_id: string,
    condition_group_id: string,
    type: string,
): Promise<LRegistrationBatchResponse> => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/assess/hev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.calculation_id)}
    /datasets/units
    /${encodeURIComponent(unit_id)}
    /${encodeURIComponent(gear_id)}
    /${encodeURIComponent(condition_group_id)}
    ?type=${encodeURIComponent(type)}`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        },
        body: JSON.stringify({ "registration_method": "excel" }),
    });

    const body = await response.json().catch(() => ({}));
    const payload = typeof body === "object" && body !== null ? body : {};
    return { status: response.status, ...payload } as LRegistrationBatchResponse;
};

// HEV: 他計算セットから全データコピー登録 — .../datasets/<gear_id>/all（1 gear 分）
// BEV の datasets/all（gear なし）とは別エンドポイント。「全データコピー」は ProjectCopy 側で
// コピー元の登録済み gear を列挙し gear ごとに本関数を呼ぶ（gear ループは FE 責務）。
// 最新 BE は HEV copy で config.gear_id が必須（無いと 400）。all-copy では URL gear と同値を送る。
export const submitAllCopyProjectHev = async (
    authToken: string,
    config: Config,
    fromConfig: Config,
    gear_id: string,
    type: string = "target",
): Promise<LRegistrationBatchResponse> => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/assess/hev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.calculation_id)}
    /datasets/${encodeURIComponent(gear_id)}/all?type=${encodeURIComponent(type)}`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        },
        body: JSON.stringify({
            "registration_method": "copy",
            "config": {
                "project_id": fromConfig.project_id,
                "destination_id": fromConfig.destination_id,
                "lot_id": fromConfig.lot_id,
                "phase_id": fromConfig.phase_id,
                "calculation_id": fromConfig.calculation_id,
                // #20: HEV copy はコピー元 gear が必須。all-copy は URL gear と同値を送る
                "gear_id": gear_id,
                // #22: BEV/HEV 共存時の曖昧性を解消するため powertrain を明示送信する
                "powertrain": "hev",
            }
        }),
    });

    const body = await response.json().catch(() => ({}));
    const payload = typeof body === "object" && body !== null ? body : {};
    return { status: response.status, ...payload } as LRegistrationBatchResponse;
};

// HEV: 他計算セットからユニット別データコピー登録 — .../datasets/units/<unit>/<gear>
// gear は URL のみ（source config は BEV と同じ 5 ID。assess.yaml datasets/all の copy 例に倣い gear は body 非搭載）。
export const submitUnitCopyProjectHev = async (
    authToken: string,
    config: Config,
    fromConfig: Config,
    gear_id: string,
    src_gear_id: string,
    unit_id: string,
    type: string = "target",
): Promise<LRegistrationBatchResponse> => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/assess/hev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.calculation_id)}
    /datasets/units
    /${encodeURIComponent(unit_id)}
    /${encodeURIComponent(gear_id)}
    ?type=${encodeURIComponent(type)}`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        },
        body: JSON.stringify(
            {
                "registration_method": "copy",
                "config": {
                    "project_id": fromConfig.project_id,
                    "destination_id": fromConfig.destination_id,
                    "lot_id": fromConfig.lot_id,
                    "phase_id": fromConfig.phase_id,
                    "calculation_id": fromConfig.calculation_id,
                    "gear_id": src_gear_id,
                    // #22: BEV/HEV 共存時の曖昧性を解消するため powertrain を明示送信する
                    "powertrain": "hev",
                }
            }
        ),
    });

    const body = await response.json().catch(() => ({}));
    const payload = typeof body === "object" && body !== null ? body : {};
    return { status: response.status, ...payload } as LRegistrationBatchResponse;
};

// HEV: 他計算セットから個別データコピー登録 — .../datasets/units/<unit>/<gear>/<condition_group_id>
export const submitSubCopyProjectHev = async (
    authToken: string,
    config: Config,
    fromConfig: Config,
    gear_id: string,
    src_gear_id: string,
    unit_id: string,
    condition_group_id: string,
    type: string = "target",
): Promise<LRegistrationBatchResponse> => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/assess/hev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.calculation_id)}
    /datasets/units
    /${encodeURIComponent(unit_id)}
    /${encodeURIComponent(gear_id)}
    /${encodeURIComponent(condition_group_id)}
    ?type=${encodeURIComponent(type)}`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        },
        body: JSON.stringify(
            {
                "registration_method": "copy",
                "config": {
                    "project_id": fromConfig.project_id,
                    "destination_id": fromConfig.destination_id,
                    "lot_id": fromConfig.lot_id,
                    "phase_id": fromConfig.phase_id,
                    "calculation_id": fromConfig.calculation_id,
                    "gear_id": src_gear_id,
                    // #22: BEV/HEV 共存時の曖昧性を解消するため powertrain を明示送信する
                    "powertrain": "hev",
                }
            }
        ),
    });

    const body = await response.json().catch(() => ({}));
    const payload = typeof body === "object" && body !== null ? body : {};
    return { status: response.status, ...payload } as LRegistrationBatchResponse;
};
